/**
 * Human-readable labels for the six fixed keyword weight tiers (WHEEL-382).
 *
 * The scoring cascade only knows these six values. Copy is the corrected label
 * set from the WHEEL-382 ticket comments; keep it in sync there if it changes.
 */
export interface KeywordWeightLabel {
  weight: number;
  /** Compact label for dropdown rows and badges. */
  short: string;
  /** Longer label for legends and tooltips. */
  full: string;
  /** What it takes for a keyword with this weight to flag a threat. */
  description: string;
}

export const KEYWORD_WEIGHT_LABELS: readonly KeywordWeightLabel[] = [
  {
    weight: 1,
    short: 'Alone, typos included',
    full: 'Flags alone, including typos',
    description:
      'The keyword on its own is enough, and so is any typo of it. Other words in the domain only raise the priority, they are never required.',
  },
  {
    weight: 0.99,
    short: 'Alone, exact only',
    full: 'Flags alone; typos need support',
    description:
      'The keyword on its own is enough. A typo of it is not: a typo needs a supporting word alongside it (generic, company, protection or industry).',
  },
  {
    weight: 0.95,
    short: 'Keyword + digit or dash',
    full: 'Needs the keyword plus a digit or dash in the domain',
    description:
      'The keyword only counts when the domain also contains a digit or a dash, as in paypal-1.com. Supporting words raise the priority but cannot substitute for the digit or dash. A typo works differently: it needs a supporting word instead.',
  },
  {
    weight: 0.9,
    short: 'Needs any support',
    full: 'Needs any supporting word',
    description:
      'The keyword, or a typo of it, plus at least one supporting word of any kind (generic, company, protection or industry).',
  },
  {
    weight: 0.8,
    short: 'Needs specific support',
    full: 'Needs a brand-specific supporting word',
    description:
      'The keyword, or a typo of it, plus a company, protection or industry word. A generic word does not count. That exclusion is the only difference from 0.9.',
  },
  {
    weight: 0.5,
    short: 'Needs main keyword',
    full: 'Needs another main keyword',
    description:
      "The keyword only counts alongside another main keyword: either one weighted above 0.5, or all of the protection's remaining main keywords. Supporting words are not enough on their own.",
  },
];

const byWeight = new Map(KEYWORD_WEIGHT_LABELS.map(l => [l.weight, l]));

/**
 * Returns the label for a canonical weight, or `undefined` for anything else
 * (e.g. a legacy value stored before weights were snapped to the six tiers).
 */
export const getKeywordWeightLabel = (
  weight: number | string | null | undefined,
): KeywordWeightLabel | undefined => {
  if (weight === null || weight === undefined || weight === '')
    return undefined;
  const n = Number(weight);
  return Number.isNaN(n) ? undefined : byWeight.get(n);
};
